import { Loader2, ReceiptText, RotateCw } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";

import { useCardTransactionDetail } from "../hooks/useCardTransactionDetail";
import { CardTransactionDetailBody } from "./CardTransactionDetailBody";

interface CardTransactionDetailDrawerProps {
  open: boolean;
  cardId: string;
  transactionId: string | null;
  onOpenChange: (open: boolean) => void;
}

export const CardTransactionDetailDrawer = ({
  open,
  cardId,
  transactionId,
  onOpenChange,
}: CardTransactionDetailDrawerProps) => {
  const { t, activeLanguage } = useTranslations("cards");
  const { detail, isLoading, error, refetch } = useCardTransactionDetail({ cardId, transactionId });

  const handleClose = () => onOpenChange(false);

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={<span className="text-[15px] font-semibold text-foreground">{t("txn_detail_title")}</span>}
    >
      {isLoading || (!detail && !error) ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
          <span className="text-sm">{t("txn_detail_loading")}</span>
        </div>
      ) : error ? (
        <div className="flex flex-1 items-center justify-center py-10">
          <ColocaEmpty
            icon={ReceiptText}
            title={t("txn_detail_error_title")}
            description={t("txn_detail_error_description")}
          >
            <ColocaButton variant="secondary" size="sm" icon={RotateCw} onClick={refetch}>
              {t("txn_detail_retry")}
            </ColocaButton>
          </ColocaEmpty>
        </div>
      ) : detail ? (
        <CardTransactionDetailBody detail={detail} locale={activeLanguage} onClose={handleClose} />
      ) : null}
    </ColocaDrawer>
  );
};
